import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PER_PAGE, PER_PAGE_OPTIONS } from '@microsec/constants';
import {
  INTERFACE_TYPES,
  INTERFACE_TYPE_LABELS,
  LEVEL_OPTIONS,
  COMPLIANCE_STANDARDS,
  COMPLIANCE_STANDARD_OPTIONS,
  PROJECT_MANAGEMENT_CONSTANTS,
  TLS_CONFIG_FIELDS,
} from '@ids-constants';
import { BaseComponent } from '@ids-components';
import { ConnectionService, TargetDeviceService } from '@ids-services';
import { fadeAnimation, verticalSlideAnimation } from '@microsec/animations';
import { MODULES } from '../../../modules';
import { ORGANIZATION_ID_PARAM_ROUTE, PROJECT_ID_PARAM_ROUTE } from '@microsec/constants';
import { ArrayMapPipe, ConstantPipe, MomentPipe } from '@ids-pipes';
import { NETWORK_SETTINGS_CONSTANTS } from '../../../project-management/configuration/network-settings/network-settings.config';

import { finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { CardComponent, CommonTableComponent, CustomTimelineComponent } from '@microsec/components';
import { ChipModule } from 'primeng/chip';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { TableModule } from 'primeng/table';

@Component({
  selector: 'app-device-overview',
  templateUrl: './device-overview.component.html',
  styleUrls: ['./device-overview.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ChipModule,
    TableModule,
    AccordionModule,
    SelectButtonModule,
    CardComponent,
    CustomTimelineComponent,
    CommonTableComponent,
    ArrayMapPipe,
    ConstantPipe,
    MomentPipe,
  ],
  providers: [MomentPipe],
  animations: [fadeAnimation, verticalSlideAnimation],
})
export class DeviceOverviewComponent extends BaseComponent implements OnInit {
  isLoading = false;

  PER_PAGE = PER_PAGE;

  PER_PAGE_OPTIONS = PER_PAGE_OPTIONS;

  TLS_CONFIG_FIELDS = TLS_CONFIG_FIELDS;

  _device = false;

  get device() {
    return this._device;
  }

  @Input() set device(value: any) {
    this._device = value;
    if (!!this._device) {
      this.setDevice();
    }
  }

  @Output() goToTab: EventEmitter<any> = new EventEmitter<any>();

  connections: any[] = [];

  interfaceMainCols: any[] = [{ field: 'interface', header: 'Interface' }];

  canDevices: any[] = [];

  LEVEL_OPTIONS = LEVEL_OPTIONS;

  deviceTimelineEvents: any[] = [];

  showSoftwareAndHardwareDetails = false;

  detailsViewOptions = [
    { value: 'hardware', label: 'Hardware' },
    { value: 'software', label: 'Software' },
  ];

  selectedDetailsView = 'hardware';

  viewAllIps = false;

  COMPLIANCE_STANDARDS = COMPLIANCE_STANDARDS;

  COMPLIANCE_STANDARD_OPTIONS = COMPLIANCE_STANDARD_OPTIONS;

  constructor(
    private targetDeviceSrv: TargetDeviceService,
    private connectionSrv: ConnectionService,
    private momentPipe: MomentPipe,
  ) {
    super();
  }

  async ngOnInit() {
    await this.prepareConfigs();
    this.getConnections();
    this.setDevice();
    this.subscriptions.forEach((s) => s.unsubscribe());
    const subscription = this.targetDeviceSrv.refreshObs.subscribe((rs) => {
      if (!!rs) {
        this.setDevice();
      }
    });
    this.subscriptions.push(subscription);
  }

  setDevice() {
    const interfaces: any[] = [];
    Object.values(INTERFACE_TYPES).forEach((key) => {
      if (!!this.device[key]) {
        const children: any[] = [];
        Object.keys(this.device[key]).forEach((childKey: string) => {
          const newData = {
            source: childKey,
            destination: this.device[key][childKey] || [],
          };
          children.push(newData);
        });
        if (!!children.length) {
          interfaces.push({ interface: INTERFACE_TYPE_LABELS[key] || key, children: children });
        }
      }
    });
    this.device.interfaces = interfaces;
    if (this.connections?.length > 0) {
      const deviceConnections = this.connections
        .filter((con) => ((this.device.connection_ids as any[]) || []).includes(con.id))
        .map((con) => ({ id: con.id, name: con.name }));
      this.device.connections = deviceConnections;
    }
    this.device.macAddress = Object.keys(this.device?.eth || {})?.[0] || '';
    this.device.ipAddress = Object.keys(this.device.ip || {})?.join(', ') || '';
    this.device.bleAddress = Object.keys(this.device?.ble || {})?.[0] || '';
    this.device.unitId = Object.keys(this.device?.modbus_rtu || {})?.join(', ') || '';
    this.device.bacnetNumber = Object.keys(this.device?.bacnet_ms_tp || {})?.join(', ') || '';
    this.device.dnp3Id = Object.keys(this.device?.dnp3 || {})?.join(', ') || '';
    this.deviceTimelineEvents = this.extractTimelineEvents(this.device);
  }

  getConnections() {
    this.isLoading = true;
    this.connectionSrv
      .getConnections(this.breadcrumbConfig?.organizationId, this.breadcrumbConfig?.projectId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (rs: any) => {
          this.connections = (rs.data as any[]) || [];

          const deviceConnections = this.connections
            .filter((con) => ((this.device.connection_ids as any[]) || []).includes(con.id))
            .map((con) => ({ id: con.id, name: con.name }));
          this.device.connections = deviceConnections;
        },
        error: (err: any) => {
          this.showErrorMessage(err);
        },
      });
  }

  extractTimelineEvents(device: any) {
    const timelineEvents = [];
    if (!!device.created && !!device.is_manually_added) {
      timelineEvents.push({
        title: 'Created At',
        substring1: this.momentPipe?.transform(device.created).split(' ')?.[0],
        substring2: this.momentPipe?.transform(device.created).split(' ')?.[1],
        timestamp: this.momentPipe?.transform(device.created),
      });
    }
    if (!!device.first_seen) {
      timelineEvents.push({
        title: 'First Seen',
        substring1: this.momentPipe?.transform(device.first_seen).split(' ')?.[0],
        substring2: this.momentPipe?.transform(device.first_seen).split(' ')?.[1],
        timestamp: this.momentPipe?.transform(device.first_seen),
      });
    }
    if (!!device.last_seen) {
      timelineEvents.push({
        title: 'Last Seen',
        substring1: this.momentPipe?.transform(device.last_seen).split(' ')?.[0],
        substring2: this.momentPipe?.transform(device.last_seen).split(' ')?.[1],
        timestamp: this.momentPipe?.transform(device.last_seen),
      });
    }
    if (!!device.nmap?.uptime?.['@lastboot']) {
      timelineEvents.push({
        title: 'Last Boot',
        substring1: this.momentPipe?.transform(device.nmap.uptime['@lastboot']).split(' ')?.[0],
        substring2: this.momentPipe?.transform(device.nmap.uptime['@lastboot']).split(' ')?.[1],
        timestamp: this.momentPipe?.transform(device.nmap.uptime['@lastboot']),
      });
    }
    const sortedTimelineEvents = this.util.sortObjectArray(timelineEvents, 'timestamp', true);
    return sortedTimelineEvents;
  }

  toggleSoftwareAndHardwareDetails() {
    this.showSoftwareAndHardwareDetails = !this.showSoftwareAndHardwareDetails;
  }

  goToNetworkMapPage() {
    this.targetDeviceSrv.selected = this.device || null;
    this.changeRoute(
      `${MODULES.PROJECT_MANAGEMENT.ROUTE.replace(ORGANIZATION_ID_PARAM_ROUTE, this.breadcrumbConfig?.organizationId?.toString()).replace(
        PROJECT_ID_PARAM_ROUTE,
        this.breadcrumbConfig?.projectId?.toString(),
      )}/${PROJECT_MANAGEMENT_CONSTANTS.NETWORK_MAP.ROUTE}`,
    );
  }

  goToConnectionPage(connection_id: any) {
    this.connectionSrv.selected = !!connection_id ? { id: connection_id } : null;
    this.changeRoute(
      `${MODULES.PROJECT_MANAGEMENT.ROUTE.replace(ORGANIZATION_ID_PARAM_ROUTE, this.breadcrumbConfig?.organizationId?.toString()).replace(
        PROJECT_ID_PARAM_ROUTE,
        this.breadcrumbConfig?.projectId?.toString(),
      )}/${PROJECT_MANAGEMENT_CONSTANTS.NETWORK_SETTINGS.ROUTE}/${NETWORK_SETTINGS_CONSTANTS.NETWORKS.ROUTE}`,
    );
  }
}
